import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  examOverview: null,
  currentSection: null,
  currentSectionIndex: 0,
  timeRemaining: 0,
  totalExamTimeMinutes: 0,
  answeredQuestions: {},
  answersQueue: [],
  loadingSection: false,
  pendingSubmissionCount: 0,
  examCompleted: false,
  lastError: null,
  startTestData: null,
  uploadedAudio: null,
  missingQuestions: [],
};

const examSlice = createSlice({
  name: "exam",
  initialState,
  reducers: {
    setExamOverview(state, action) {
      state.examOverview = action.payload;
      state.currentSectionIndex = action.payload.current_section_index ?? 0;
      state.timeRemaining =
        action.payload.time_remaining_seconds ?? state.timeRemaining;
      state.totalExamTimeMinutes =
        action.payload.total_exam_time ?? state.totalExamTimeMinutes;
    },
    setCurrentSection(state, action) {
      state.currentSection = action.payload;
      state.loadingSection = false;
      // reset any UI-per-question local progress if needed
    },
    setLoadingSection(state, action) {
      state.loadingSection = !!action.payload;
    },
    updateTimeRemaining(state, action) {
      state.timeRemaining = action.payload;
    },
    tickOneSecond(state) {
      if (state.timeRemaining > 0) state.timeRemaining -= 1;
    },
    markAnswerPending(state, action) {
      const { assignment_question_id, payload } = action.payload;
      state.answeredQuestions[assignment_question_id] = {
        ...payload,
        status: "pending",
      };
      state.pendingSubmissionCount += 1;
    },
    markAnswerSaved(state, action) {
      const { assignment_question_id } = action.payload;
      const entry = state.answeredQuestions[assignment_question_id];
      if (entry) {
        entry.status = "saved";
      } else {
        // still record it
        state.answeredQuestions[assignment_question_id] = { status: "saved" };
      }
      if (state.pendingSubmissionCount > 0) state.pendingSubmissionCount -= 1;
    },
    enqueueAnswer(state, action) {
      state.answersQueue.push(action.payload);
    },
    clearAnswerQueue(state) {
      state.answersQueue = [];
    },
    addLocalAnswer(state, action) {
      // local UI marking when user answers a question
      const { assignment_question_id, question_id, answer } = action.payload;
      state.answeredQuestions[assignment_question_id] = {
        question_id,
        answer,
        status: "local",
      };
    },
    setExamComplete(state, action) {
      state.examCompleted = true;
    },
    setLastError(state, action) {
      state.lastError = action.payload;
    },
    resetExamState(state) {
      return initialState;
    },
    setMissingQuestions(state, action) {
      state.missingQuestions = action.payload || [];
    },
  },
  extraReducers: (builder) => {
    builder
      // ⭐ STORE START TEST API RESPONSE
      .addCase("START_TEST_SUCCESSFULLY", (state, action) => {
        state.startTestData = action.payload; // store API response
      })

      // ⭐ STORE UPLOADED AUDIO RESPONSE
      .addCase("UPLOAD_AUDIO_SUCCESS", (state, action) => {
        state.uploadedAudio = action.payload;
      });
  },
});

export const {
  setExamOverview,
  setCurrentSection,
  setLoadingSection,
  updateTimeRemaining,
  tickOneSecond,
  markAnswerPending,
  markAnswerSaved,
  enqueueAnswer,
  clearAnswerQueue,
  addLocalAnswer,
  setExamComplete,
  setLastError,
  resetExamState,
  setMissingQuestions,
} = examSlice.actions;

export default examSlice.reducer;
